import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useT } from '../lib/i18n';
import { ResponsiveContainer, ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, ChartTooltip, PillToggle, PageHeader, QueryState, INPUT, CALUM, SERIES, Loader, ErrorBox } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import { useDataRange } from '../hooks/useDataRange';
import api from '../lib/api';
import { formatCurrency, formatCurrency2, formatNumber, formatMonth, formatDate, weekdayLabel, weekdayShort } from '../lib/format';
import { todayISO } from '../lib/day';

// Report product groups in display order: [key, label, description] (Dutch; t() localises).
const GROUPS = [
  ['cookies', 'Koekjes', 'Cookie, Cookie Matcha, Cookies Box, Sablés'],
  ['breakfast', 'Ontbijt', 'De volledige categorie Food/Breakfast (ontbijtmenu)'],
  ['pastries', 'Gebak', 'Zoet uit Food/Sweets, zonder de koekjes'],
  ['vegetable_plate', 'Vegetable Plate', "Het product 'Vegetable Plate' (assiette de légumes)"],
  ['savoury', 'Hartige lunch', 'Hartig eten buiten ontbijt, zoet en de Vegetable Plate'],
  ['ice_cream', 'IJs', 'De categorie Glaces — bolletjes, boules en coupes'],
  ['cappuccino', 'Cappuccino', 'Alle cappuccino’s'],
  ['espresso', 'Espresso', 'Koffies op basis van espresso (ook dubbel, macchiato, iced)'],
  ['coffee', 'Koffie (totaal)', 'De subcategorie Drinks/Coffee'],
  ['matcha', 'Matcha + Genmaicha', 'Matcha- en genmaicha-dranken (zonder Cookie Matcha)'],
  ['other_drinks', 'Overige dranken', 'Frisdranken, thee en specials — dranken buiten koffie en matcha'],
  ['tea', 'Thee', 'De subcategorie Drinks/Tea'],
  ['drinks', 'Dranken', 'Alles in de categorie Drinks (+ drank-extra’s)'],
  ['food', 'Voeding', 'De categorie Food — alles wat eetbaar is'],
];
const groupMeta = (k) => GROUPS.find((g) => g[0] === k) || [k, k, ''];
// The all-products overview partitions every product into its most specific
// group; the container groups (coffee/drinks/food/cafe) then only hold what no
// narrower group claimed, so they get an "(overige)" label there.
const OVERVIEW_LABELS = { coffee: 'Koffie (overige)', drinks: 'Dranken (overige)', food: 'Voeding (overige)',
  cafe: 'Café (overige)', retail: 'Winkel (niet-café)', other: 'Overige' };
const overviewLabel = (k) => OVERVIEW_LABELS[k] || groupMeta(k)[1];
const hasDetail = (k) => GROUPS.some((g) => g[0] === k);

// Language-aware "jan–mrt" from a list of "YYYY-MM" strings.
const monthShort = (ym) => formatMonth(ym).replace(/\s'\d\d$/, '');
const monthsLabel = (ms) => ms?.length ? `${monthShort(ms[0])}–${monthShort(ms[ms.length - 1])}` : '';
// "1 jan 2026 – 24 aug 2026" from a backend `window`.
const winLabel = (w) => (w?.start && w?.end ? `${formatDate(w.start)} – ${formatDate(w.end)}` : '');
const Pct = ({ v }) => {
  const t = useT();
  return v == null ? <span className="text-slate-400">{t('nieuw')}</span> : (
    <span className={v >= 0 ? 'text-emerald-600' : 'text-red-500'}>{v >= 0 ? '+' : ''}{String(Math.round(v))}%</span>
  );
};

/**
 * When a selection actually sells, across the day and the week.
 *
 * Gold bars are this selection, the grey dashed line is the café as a whole —
 * both as a share of their own total. That comparison is what makes the answer
 * meaningful: everything peaks at the busiest hour, so a dish only counts as a
 * morning dish if it takes a bigger share of the morning than the café does.
 */
function TimingCard({ d, t }) {
  const hours = (d.by_hour || []).map((h) => ({ ...h, label: h.label.slice(0, 2) + 'h' }));
  const skew = d.skew_daypart;
  const lead = skew && Math.abs(skew.diff) >= 5
    ? `${t('Verkoopt vooral in de')} ${skew.label.split(' (')[0].toLowerCase()} — ${skew.pct}% ${t('van de verkopen tegenover')} ${skew.cafe_pct}% ${t('voor het café als geheel')}`
    : t('Volgt ongeveer het ritme van het café.');
  return (
    <SectionCard className="mb-4" title={`${t('Wanneer verkopen we dit?')}${d.labels?.length ? ` — ${d.labels.join(' + ')}` : ''}`}
      subtitle={`${formatNumber(d.total_units)} ${t('stuks')} · ${lead}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">{t('Per uur')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={hours} margin={{ left: 4, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="pct" name={t('deze selectie')} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
              <Line dataKey="cafe_pct" name={t('het café')} stroke="#94a3b8" strokeWidth={1.5}
                strokeDasharray="5 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">{t('Per dagdeel')}</div>
          <div className="space-y-2">
            {(d.dayparts || []).map((p) => (
              <div key={p.key}>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-slate-600">{t(p.label)}</span>
                  <span className="tabular-nums font-semibold text-slate-800">{p.pct}%</span>
                </div>
                <div className="relative h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: CALUM.gold }} />
                  <div className="absolute top-0 h-full border-r-2 border-slate-400"
                    style={{ left: `${p.cafe_pct}%` }} title={`${t('het café')}: ${p.cafe_pct}%`} />
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums">
                  {formatNumber(p.units)} {t('stuks')} · {t('café')} {p.cafe_pct}%
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mt-3 mb-1">{t('Per weekdag')}</div>
          <div className="flex items-end gap-1 h-16">
            {(d.by_weekday || []).map((w) => {
              const max = Math.max(1, ...d.by_weekday.map((x) => x.pct));
              return (
                <div key={w.weekday_nl} className="flex-1 flex flex-col items-center gap-0.5"
                  title={`${w.weekday_nl}: ${formatNumber(w.units)} ${t('stuks')} (${w.pct}%)`}>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(3, (w.pct / max) * 40)}px`, background: CALUM.green, opacity: 0.8 }} />
                  <span className="text-[9px] text-slate-400">{weekdayShort(w.weekday_nl)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

export default function Products() {
  const t = useT();
  const [group, setGroup] = useState('all');          // 'all' = overview of every product
  const [cmp, setCmp] = useState('yoy');              // benchmark for the overview: 'yoy' | 'prev'
  const [metric, setMetric] = useState('units');
  // custom selection
  const [picked, setPicked] = useState([]);          // tokens: 'p:<id>' | 'l1:<cat>' | 'l2:<sub>'
  const [mode, setMode] = useState('combined');      // 'combined' | 'compare'
  const [selOpen, setSelOpen] = useState(false);
  const [search, setSearch] = useState('');
  const custom = picked.length > 0;
  const selRef = useRef(null);
  useEffect(() => {
    if (!selOpen) return;
    const onDoc = (e) => { if (selRef.current && !selRef.current.contains(e.target)) setSelOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [selOpen]);

  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };
  const dataRange = useDataRange();
  const rdeps = [range.start_date, range.end_date];

  const isAll = group === 'all';
  const tr = useApi(() => (isAll ? Promise.resolve(null) : api.productTrend(group, p)), [group, ...rdeps]);
  const gi = useApi(() => (isAll ? Promise.resolve(null) : api.groupInsights(group)), [group]);
  const ov = useApi(() => (isAll && !custom ? api.productOverview({ cmp, ...p }) : Promise.resolve(null)),
                    [isAll, custom, cmp, ...rdeps]);
  const tf = useApi(() => api.topFoodProducts({ limit: 10, ...p }), rdeps);
  const cat = useApi(() => api.productsTable(), []);                 // catalogue for the dropdown
  const ct = useApi(() => (custom ? api.customTrend(picked, mode, p) : Promise.resolve(null)),
                    [picked.join(','), mode, ...rdeps]);
  const tm = useApi(() => (custom ? api.productTiming(picked, p) : Promise.resolve(null)),
                    [picked.join(','), ...rdeps]);

  const monthly = (tr.data?.monthly || []).map((m) => ({ ...m, label: formatMonth(m.month) }));
  const [, gLabel, gDesc] = groupMeta(group);
  const groupLabel = t(gLabel);

  // build dropdown options (products + L1/L2 categories) from the catalogue
  const opts = useMemo(() => {
    const prods = (cat.data?.products || []).filter((p) => p.product_id);
    const l1 = {}, l2 = {};
    prods.forEach((p) => {
      l1[p.category_l1] = (l1[p.category_l1] || 0) + (p.units || 0);
      if (p.category_l2) l2[p.category_l2] = (l2[p.category_l2] || 0) + (p.units || 0);
    });
    const cats = [
      ...Object.entries(l1).map(([name, units]) => ({ token: `l1:${name}`, name, units, kind: 'categorie' })),
      ...Object.entries(l2).map(([name, units]) => ({ token: `l2:${name}`, name, units, kind: 'subcategorie' })),
    ].sort((a, b) => b.units - a.units);
    const products = prods.map((p) => ({ token: `p:${p.product_id}`, name: p.name, units: p.units || 0,
      sub: [p.category_l1, p.category_l2].filter(Boolean).join(' · ') })).sort((a, b) => b.units - a.units);
    return { cats, products };
  }, [cat.data]);

  const labelFor = (tok) => {
    if (tok.startsWith('p:')) return opts.products.find((x) => x.token === tok)?.name || tok.slice(2);
    return tok.slice(3);
  };
  const toggle = (tok) => setPicked((cur) => cur.includes(tok) ? cur.filter((t) => t !== tok) : [...cur, tok]);
  const q = search.trim().toLowerCase();
  const showCats = opts.cats.filter((c) => !q || c.name.toLowerCase().includes(q));
  const showProds = opts.products.filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 80);

  // combined chart data + compare (merge series by month)
  const customMonthly = (ct.data?.monthly || []).map((m) => ({ ...m, label: formatMonth(m.month) }));
  const compareData = useMemo(() => {
    if (ct.data?.mode !== 'compare') return [];
    const months = ct.data.series?.[0]?.monthly.map((m) => m.month) || [];
    return months.map((mo) => {
      const row = { label: formatMonth(mo) };
      ct.data.series.forEach((s) => { row[s.label] = (s.monthly.find((x) => x.month === mo) || {})[metric] || 0; });
      return row;
    });
  }, [ct.data, metric]);

  // Subtitle of the top-food card follows the window the backend actually used.
  const tfSubtitle = tf.data?.window
    ? `${winLabel(tf.data.window)} · Δ = ${t('vs de periode ervoor')} (${winLabel(tf.data.prev_window)}, ${t('naamwissels gekoppeld')}) · ${t("'nieuw' = toen nog niet verkocht")}`
    : `Δ = ${t('vs de even lange periode ervoor')} · ${t("'nieuw' = toen nog niet verkocht")}`;
  const giMonths = gi.data?.months || [];

  return (
    <PageShell page="Productgroepen" accent="gold">
      <PageHeader title={t("Productgroepen")} subtitle={t("Alle producten met hun verkoop t.o.v. een benchmarkperiode, en de ontwikkeling per productgroep.")}>
        <DateRangeControl value={range} onChange={setRange} dataRange={dataRange} />
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-3">
        {[['all', 'Alle producten'], ...GROUPS].map(([k, label]) => (
          <button key={k} onClick={() => { setGroup(k); setPicked([]); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              !custom && group === k ? 'bg-[#114817] text-white border-[#114817]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#cf9a40]'}`}>
            {t(label)}
          </button>
        ))}
      </div>

      {/* ---- custom product/category selector ---- */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={selRef}>
            <button onClick={() => setSelOpen((o) => !o)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                custom ? 'bg-[#cf9a40] text-white border-[#cf9a40]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#cf9a40]'}`}>
              <Search className="h-3.5 w-3.5" /> {t('Kies producten / categorieën')} <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {selOpen && (
              <div className="absolute z-30 mt-1 w-[min(20rem,calc(100vw-2rem))] max-h-96 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="sticky top-0 bg-white p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                    <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('zoek…')}
                      className={`${INPUT} w-full pl-8`} />
                  </div>
                </div>
                {showCats.length > 0 && (
                  <div className="px-2 pt-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-1 mb-1">{t('Categorieën')}</div>
                    {showCats.map((c) => (
                      <Opt key={c.token} checked={picked.includes(c.token)} onClick={() => toggle(c.token)}
                        name={c.name} sub={t(c.kind)} units={c.units} />
                    ))}
                  </div>
                )}
                <div className="px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-1 mb-1">{t('Producten')}</div>
                  {showProds.map((p) => (
                    <Opt key={p.token} checked={picked.includes(p.token)} onClick={() => toggle(p.token)}
                      name={p.name} sub={p.sub} units={p.units} />
                  ))}
                  {!showProds.length && <div className="text-xs text-slate-400 px-1 py-2">{t('Niets gevonden.')}</div>}
                </div>
              </div>
            )}
          </div>
          {custom && (
            <>
              <PillToggle value={mode} onChange={setMode}
                options={[{ value: 'combined', label: t('Samen') }, { value: 'compare', label: t('Vergelijk') }]} />
              <button onClick={() => setPicked([])} className="text-xs text-slate-500 hover:text-red-500 underline">{t('wis selectie')}</button>
            </>
          )}
        </div>
        {custom && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {picked.map((tok) => (
              <span key={tok} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#114817]/10 text-xs text-[#114817]">
                {labelFor(tok)}
                <button onClick={() => toggle(tok)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ---- custom view ---- */}
      {custom && (
        <>
          {tm.data?.total_units > 0 && <TimingCard d={tm.data} t={t} />}
          {ct.error && <ErrorBox error={ct.error} onRetry={ct.reload} />}
          {ct.loading ? <Loader /> : ct.data && (
            <>
              {ct.data.mode === 'combined' ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                    <KpiTile label={t('Verkochte stuks')} value={formatNumber(ct.data.total_units)} sub={`${picked.length} ${t('geselecteerd')}`} accent="gold" />
                    <KpiTile label={t('Omzet (incl. btw)')} value={formatCurrency(ct.data.total_revenue)} sub={t('eigen selectie')} accent="green" />
                    <KpiTile label={t('Gem. prijs per stuk')} value={ct.data.total_units ? formatCurrency(ct.data.total_revenue / ct.data.total_units) : '—'} sub={t('over de selectie')} accent="mint" />
                  </div>
                  <SectionCard title={t('Ontwikkeling — eigen selectie (samen)')} subtitle={picked.map(labelFor).join(' + ')}
                    right={<PillToggle value={metric} onChange={setMetric} options={[{ value: 'units', label: t('Stuks') }, { value: 'revenue', label: t('Omzet') }]} />}>
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={customMonthly} margin={{ left: 4, right: 8, top: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'revenue' ? `€${(v / 1000).toFixed(0)}k` : formatNumber(v)} />
                        <Tooltip content={<ChartTooltip money={['revenue']} />} />
                        <Bar dataKey={metric} name={metric === 'revenue' ? t('Omzet') : t('Stuks')} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                        <Line dataKey={metric} name={t('trend')} stroke={CALUM.green} strokeWidth={2} dot={false} legendType="none" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </SectionCard>
                </>
              ) : (
                <SectionCard title={t('Vergelijking — per selectie')} subtitle={t('Elke lijn = één product/categorie')}
                  right={<PillToggle value={metric} onChange={setMetric} options={[{ value: 'units', label: t('Stuks') }, { value: 'revenue', label: t('Omzet') }]} />}>
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={compareData} margin={{ left: 4, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'revenue' ? `€${(v / 1000).toFixed(0)}k` : formatNumber(v)} />
                      <Tooltip content={<ChartTooltip money={metric === 'revenue' ? (ct.data.series || []).map((s) => s.label) : []} />} />
                      <Legend />
                      {(ct.data.series || []).map((s, i) => (
                        <Line key={s.label} dataKey={s.label} stroke={SERIES[i % SERIES.length]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm mt-3 min-w-[360px]">
                      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                        <th className="py-1">{t('Selectie')}</th><th className="text-right">{t('Stuks')}</th><th className="text-right">{t('Omzet')}</th><th className="text-right">{t('€/stuk')}</th>
                      </tr></thead>
                      <tbody>
                        {(ct.data.series || []).map((s, i) => (
                          <tr key={s.label} className="border-t border-slate-100">
                            <td className="py-1.5"><span className="flex items-center gap-2 min-w-0"><span className="inline-block w-2 h-2 shrink-0 rounded-full" style={{ background: SERIES[i % SERIES.length] }} /><span className="block truncate max-w-[200px]" title={s.label}>{s.label}</span></span></td>
                            <td className="text-right tabular-nums">{formatNumber(s.total_units)}</td>
                            <td className="text-right tabular-nums">{formatCurrency(s.total_revenue)}</td>
                            <td className="text-right tabular-nums">{s.total_units ? formatCurrency(s.total_revenue / s.total_units) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </>
      )}

      {/* ---- all-products overview (default) ---- */}
      {!custom && isAll && (
        <>
          {ov.error && <ErrorBox error={ov.error} onRetry={ov.reload} />}
          {ov.loading && !ov.data && <Loader />}
          {ov.data && (
            <AllProducts d={ov.data} cmp={cmp} setCmp={setCmp}
              onGroup={(k) => { setGroup(k); setPicked([]); window.scrollTo({ top: 0 }); }}
              onProduct={(pr) => { setPicked((pr.product_ids || [pr.product_id]).map((id) => `p:${id}`)); window.scrollTo({ top: 0 }); }} />
          )}
        </>
      )}

      {!custom && tr.error && <ErrorBox error={tr.error} onRetry={tr.reload} />}
      {!custom && tr.loading && !tr.data && <Loader />}

      {!custom && tr.data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <KpiTile label={t('Verkochte stuks')} value={formatNumber(tr.data.total_units)} sub={groupLabel} accent="gold" />
            <KpiTile label={t('Omzet (incl. btw)')} value={formatCurrency(tr.data.total_revenue)} sub={groupLabel} accent="green" />
            <KpiTile label={t('Producten in groep')} value={formatNumber(tr.data.members?.length)} sub={t(gDesc)} accent="mint" />
          </div>

          <SectionCard title={`${t('Ontwikkeling')} — ${groupLabel}`} subtitle={t('Per maand')}
            right={<PillToggle value={metric} onChange={setMetric}
              options={[{ value: 'units', label: t('Stuks') }, { value: 'revenue', label: t('Omzet') }]} />}>
            {tr.loading ? <Loader /> : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthly} margin={{ left: 4, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === 'revenue' ? `€${(v / 1000).toFixed(0)}k` : formatNumber(v)} />
                  <Tooltip content={<ChartTooltip money={['revenue']} />} />
                  <Bar dataKey={metric} name={metric === 'revenue' ? t('Omzet') : t('Stuks')} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                  <Line dataKey={metric} name={t('trend')} stroke={CALUM.green} strokeWidth={2} dot={false} legendType="none" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </SectionCard>

          <SectionCard className="mt-4" title={t('Kerncijfers van deze groep')}
            subtitle={giMonths.length ? `${t('Jaar-op-jaar =')} ${monthsLabel(giMonths)} ${t('vs dezelfde maanden vorig jaar — de eerlijkste vergelijking (filtert seizoen weg)')}` : ''}>
            <QueryState q={gi} isEmpty={(d) => !d.stats}>
              {(d) => (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <KpiTile label={`${t('Jaar-op-jaar')} ${monthsLabel(d.months)}`} value={<Pct v={d.stats.yoy_pct} />} sub={`${formatNumber(d.stats.units_3m)} vs ${formatNumber(d.stats.units_3m_prev)} ${t('stuks')}`} accent={d.stats.yoy_pct >= 0 ? 'green' : 'gold'} />
                  <KpiTile label={t('Op % van de bonnen')} value={`${String(d.stats.attach_pct).replace('.', ',')}%`} sub={t('afgelopen jaar')} accent="gold" />
                  <KpiTile label={t('Prijs per stuk')} value={formatCurrency(d.stats.price_per_unit)} sub={t('gemiddeld')} accent="mint" />
                  <KpiTile label={t('Aandeel café-omzet')} value={`${String(d.stats.share_of_cafe_pct).replace('.', ',')}%`} sub={t('afgelopen jaar')} accent="green" />
                  <KpiTile label={t('Sterkste dag')} value={weekdayLabel(d.stats.best_weekday_nl)} sub={`~${d.stats.best_per_day} ${t('stuks/dag')}`} accent="gold" />
                </div>
              )}
            </QueryState>
          </SectionCard>

          <SectionCard className="mt-4" title={t('Binnen de groep: wat werkt?')}
            subtitle={giMonths.length ? `${monthsLabel(giMonths)} ${giMonths[0]?.slice(0, 4)} · ${t('Jaar-op-jaar vs dezelfde maanden vorig jaar · "nieuw" = stond toen nog niet op de kaart')}` : ''}>
            <QueryState q={gi} isEmpty={(d) => !d.items?.length}>
              {(d) => (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[440px]">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-1">{t('Item')}</th><th className="text-right">{t('Stuks')}</th><th className="text-right">{t('Aandeel')}</th><th className="text-right">{t('Omzet')}</th><th className="text-right">{t('Jaar-op-jaar')}</th>
                    </tr></thead>
                    <tbody>
                      {d.items.filter((it) => it.units > 0).slice(0, 10).map((it) => (
                        <tr key={it.name} className="border-t border-slate-100">
                          <td className="py-1.5 text-slate-700"><span className="block truncate max-w-[200px]" title={it.name}>{it.name}</span></td>
                          <td className="text-right tabular-nums">{formatNumber(it.units)}</td>
                          <td className="text-right tabular-nums">{String(it.share_pct).replace('.', ',')}%</td>
                          <td className="text-right tabular-nums">{formatCurrency(it.revenue)}</td>
                          <td className="text-right tabular-nums"><Pct v={it.yoy_pct} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </QueryState>
          </SectionCard>

          <SectionCard className="mt-4" title={t('Producten in deze groep')} subtitle={`${tr.data.members?.length || 0} ${t('producten')}`}>
            <div className="flex flex-wrap gap-1.5">
              {(tr.data.members || []).map((m) => (
                <span key={m} className="px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-600">{m}</span>
              ))}
              {!tr.data.members?.length && <span className="text-sm text-slate-400">{t('Geen producten gevonden.')}</span>}
            </div>
          </SectionCard>
        </>
      )}

      <SectionCard className="mt-4" title={t('Best verkochte voedingsproducten')} subtitle={tfSubtitle}>
        <QueryState q={tf} isEmpty={(d) => !d.top_by_units?.length && !d.top_by_revenue?.length}>
          {(d) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('Op aantal stuks')}</div>
                {(d.top_by_units || []).map((it, i) => (
                  <Rank key={it.product_id} i={i} name={it.name} main={`${formatNumber(it.units)} ${t('st.')}`} sub={formatCurrency(it.revenue)} delta={it.delta_pct} />
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('Op omzet')}</div>
                {(d.top_by_revenue || []).map((it, i) => (
                  <Rank key={it.product_id} i={i} name={it.name} main={formatCurrency(it.revenue)} sub={`${formatNumber(it.units)} ${t('st.')}`} delta={it.delta_pct} />
                ))}
              </div>
            </div>
          )}
        </QueryState>
      </SectionCard>
    </PageShell>
  );
}

// One row in the product/category dropdown (checkbox + name + volume).
const Opt = ({ checked, onClick, name, sub, units }) => (
  <button onClick={onClick} className="flex w-full items-center gap-2 px-1.5 py-1.5 rounded-md hover:bg-slate-50 text-left">
    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'bg-[#114817] border-[#114817]' : 'border-slate-300'}`}>
      {checked && <span className="text-white text-[10px] leading-none">✓</span>}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm text-slate-700 truncate">{name}</span>
      {sub && <span className="block text-[10px] text-slate-400 truncate">{sub}</span>}
    </span>
    <span className="text-[10px] text-slate-400 tabular-nums shrink-0">{formatNumber(units)}</span>
  </button>
);

const MEDAL = ['#cf9a40', '#94a3b8', '#b5651d'];
const Rank = ({ i, name, main, sub, delta }) => {
  const t = useT();
  return (
    <div className="flex items-center gap-3 py-2 border-t border-slate-100 first:border-0">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: MEDAL[i] || '#cbd5e1' }}>{i + 1}</span>
      <span className="flex-1 min-w-0 text-sm text-slate-700 truncate" title={name}>{name}</span>
      <span className="text-sm font-semibold text-slate-900 tabular-nums">{main}</span>
      <span className="text-xs text-slate-400 tabular-nums w-16 text-right">{sub}</span>
      <span className="text-xs tabular-nums w-14 text-right">
        {delta == null ? <span className="text-slate-400">{t('nieuw')}</span> :
          <span className={delta >= 0 ? 'text-emerald-600' : 'text-red-500'}>{delta >= 0 ? '+' : ''}{Math.round(delta)}%</span>}
      </span>
    </div>
  );
};

// ---- all-products overview: every product vs the benchmark, per group (collapsible) or flat
const SORTERS = {
  revenue: (a, b) => b.revenue - a.revenue,
  units: (a, b) => b.units - a.units,
  delta: (a, b) => (b.revenue_chg_pct ?? -Infinity) - (a.revenue_chg_pct ?? -Infinity),
};

function AllProducts({ d, cmp, setCmp, onGroup, onProduct }) {
  const t = useT();
  const [view, setView] = useState('groups');
  const [sortKey, setSortKey] = useState('revenue');
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const ql = q.trim().toLowerCase();

  const groups = useMemo(() => (d.groups || [])
    .map((g) => ({ ...g, products: g.products.filter((p) => !ql || p.name.toLowerCase().includes(ql)).sort(SORTERS[sortKey]) }))
    .filter((g) => g.products.length), [d, ql, sortKey]);
  const flat = useMemo(() => groups.flatMap((g) => g.products.map((p) => ({ ...p, group: g.key }))).sort(SORTERS[sortKey]),
    [groups, sortKey]);

  const cmpLabel = cmp === 'prev' ? t('periode ervoor') : t('vorig jaar');
  const cov = d.cmp_coverage_pct ?? 100;
  const tot = d.totals || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile label={t('Omzet (incl. btw)')} value={formatCurrency(tot.revenue)} delta={tot.revenue_chg_pct} deltaLabel={`vs ${cmpLabel}`} accent="green" />
        <KpiTile label={t('Verkochte stuks')} value={formatNumber(tot.units)} delta={tot.units_chg_pct} deltaLabel={`vs ${cmpLabel}`} accent="gold" />
        <KpiTile label={t('Producten verkocht')} value={formatNumber(tot.products)} sub={`${formatNumber(tot.products_prev)} ${t('in de benchmark')}`} accent="mint" />
        <KpiTile label={t('Benchmark')} value={<span className="text-base">{winLabel(d.cmp_window)}</span>}
          sub={cov < 100 ? `⚠ ${t('slechts')} ${cov}% ${t('gedekt door data')}` : t('volledig gedekt door data')} accent={cov < 100 ? 'gold' : 'green'} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <PillToggle value={view} onChange={setView} options={[{ value: 'groups', label: t('Per groep') }, { value: 'list', label: t('Lijst') }]} />
        <PillToggle value={cmp} onChange={setCmp} options={[{ value: 'yoy', label: t('vs vorig jaar') }, { value: 'prev', label: t('vs periode ervoor') }]} />
        <PillToggle value={sortKey} onChange={setSortKey} options={[{ value: 'revenue', label: t('Omzet') }, { value: 'units', label: t('Stuks') }, { value: 'delta', label: 'Δ' }]} />
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Zoek product…')} className={`${INPUT} w-full pl-9`} />
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-3">{t('Klik op een groep voor de detailweergave, op een product voor zijn verloop.')} · Δ = {t('verschil met de benchmark')}</p>

      {view === 'groups' ? (
        <div className="space-y-3">
          {groups.map((g) => {
            const open = !collapsed[g.key];
            return (
              <div key={g.key} className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => setCollapsed((c) => ({ ...c, [g.key]: open }))} className="flex items-center gap-2 min-w-0 text-left">
                    {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-800 truncate">{t(overviewLabel(g.key))}</span>
                    <span className="text-xs text-slate-400 shrink-0">{g.product_count} {t('producten')}</span>
                  </button>
                  <div className="flex items-center gap-2 sm:gap-3 text-xs text-slate-500 tabular-nums shrink-0">
                    <span className="hidden sm:inline">{formatNumber(g.units)} {t('st')} <Pct v={g.units_chg_pct} /></span>
                    <span><span className="font-semibold text-slate-700">{formatCurrency(g.revenue)}</span> <Pct v={g.revenue_chg_pct} /></span>
                    {hasDetail(g.key) && (
                      <button onClick={() => onGroup(g.key)} className="text-[#114817] font-medium underline underline-offset-2 whitespace-nowrap">{t('detail')} →</button>
                    )}
                  </div>
                </div>
                {open && <div className="mt-2"><ProductRows rows={g.products} onProduct={onProduct} total={tot.revenue} /></div>}
              </div>
            );
          })}
          {!groups.length && <p className="text-sm text-slate-400">{t('Geen producten gevonden.')}</p>}
        </div>
      ) : (
        <SectionCard><ProductRows rows={flat} onProduct={onProduct} total={tot.revenue} showGroup /></SectionCard>
      )}
    </>
  );
}

function ProductRows({ rows, onProduct, total, showGroup }) {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
            <th className="py-1.5 font-medium">{t('Product')}</th>
            {showGroup && <th className="font-medium hidden md:table-cell">{t('Groep')}</th>}
            <th className="font-medium text-right">{t('Stuks')}</th><th className="font-medium text-right">Δ</th>
            <th className="font-medium text-right">{t('Omzet')}</th><th className="font-medium text-right">Δ</th>
            <th className="font-medium text-right hidden md:table-cell">{t('Gem. prijs')}</th>
            <th className="font-medium text-right hidden md:table-cell">{t('Aandeel')}</th>
            <th className="font-medium text-right hidden lg:table-cell">{t('Laatst verkocht')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.product_id || p.name} className={`border-b border-slate-50 hover:bg-slate-50/60 ${p.units ? '' : 'text-slate-400'}`}>
              <td className="py-1.5">
                <span className="flex items-center gap-1.5 min-w-0">
                  {p.product_id ? (
                    <button onClick={() => onProduct(p)} title={p.name}
                      className="truncate max-w-[220px] text-left text-slate-700 hover:text-[#114817] hover:underline underline-offset-2">{p.name}</button>
                  ) : <span className="truncate max-w-[220px]" title={p.name}>{p.name}</span>}
                  {p.n_items > 1 && <span className="text-[10px] text-slate-400 shrink-0 cursor-help" title={t('kassa-items met dezelfde naam, samengeteld')}>×{p.n_items}</span>}
                  {!p.units && <span className="text-[10px] text-slate-400 shrink-0">{t('niet meer verkocht')}</span>}
                </span>
              </td>
              {showGroup && <td className="text-xs text-slate-400 hidden md:table-cell">{t(overviewLabel(p.group))}</td>}
              <td className="text-right tabular-nums">{formatNumber(p.units)}</td>
              <td className="text-right tabular-nums text-xs"><Pct v={p.units_chg_pct} /></td>
              <td className="text-right tabular-nums font-medium">{formatCurrency(p.revenue)}</td>
              <td className="text-right tabular-nums text-xs"><Pct v={p.revenue_chg_pct} /></td>
              <td className="text-right tabular-nums text-slate-500 hidden md:table-cell">{p.units ? formatCurrency2(p.avg_price) : '—'}</td>
              <td className="text-right tabular-nums text-slate-400 hidden md:table-cell">{total ? (100 * p.revenue / total).toFixed(1).replace('.', ',') : '0'}%</td>
              <td className="text-right text-xs text-slate-400 whitespace-nowrap hidden lg:table-cell">{p.last_sold ? formatDate(p.last_sold) : '—'}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={showGroup ? 9 : 8} className="py-2 text-sm text-slate-400">{t('Geen producten gevonden.')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
